import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { useAddDiscountCodes } from '../hooks/use-discount-mutations';
import {
  generateCodesSchema,
  type GenerateCodesInput,
  type GenerateCodesOutput,
} from '../schemas/discount-form-schema';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['count', 'prefix']);

export interface GenerateCodesDialogProps {
  storeId: string;
  discountId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** The backend generates random codes after the prefix; they appear in the list once saved. */
export function GenerateCodesDialog({
  storeId,
  discountId,
  open,
  onOpenChange,
}: GenerateCodesDialogProps) {
  const { t } = useTranslation(['discounts', 'common']);
  const add = useAddDiscountCodes(storeId);
  const message = useErrorMessage();
  const form = useForm<GenerateCodesInput, unknown, GenerateCodesOutput>({
    resolver: useI18nResolver(generateCodesSchema),
    defaultValues: { count: 10, prefix: '' },
  });

  const close = () => {
    onOpenChange(false);
    add.reset();
    form.reset();
  };

  const submit = form.handleSubmit(async (values) => {
    try {
      await add.mutateAsync({
        id: discountId,
        body: { generate: { count: values.count, prefix: values.prefix.toUpperCase() } },
      });
      close();
    } catch (error) {
      if (
        applyFieldErrors(error, form.setError, {
          knownFields: KNOWN_FIELDS,
          mapField: (f) => f.replace(/^generate\./, ''),
        })
      ) {
        return;
      }
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('discounts:generate.title')}</DialogTitle>
          <DialogDescription>{t('discounts:generate.description')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="count"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('discounts:generate.count')}</FormLabel>
                    <FormControl>
                      <Input type="number" inputMode="numeric" min={1} max={500} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="prefix"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('discounts:generate.prefix')}</FormLabel>
                    <FormControl>
                      <Input autoComplete="off" className="font-mono uppercase" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <p className="text-muted-foreground -mt-2 text-xs">{t('discounts:generate.hint')}</p>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={add.isPending}>
                {add.isPending
                  ? t('discounts:generate.submitting')
                  : t('discounts:generate.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
